'use es6';

import { Map as ImmutableMap } from 'immutable';
import Exam from '../records/Exam';
import Quiz from '../records/Quiz';
const DEFAULT_ID_KEY = 'id';
export function mappifyByKey(collection, key = DEFAULT_ID_KEY) {
  return collection.reduce((map, object) => map.set(object.get(key), object), ImmutableMap());
}
export function mapToIds(object) {
  return object.get('id');
}
export function getChildrenIds(collection) {
  return collection.map(mapToIds);
}
export function normalizeContent(content) {
  if (content instanceof Quiz || content instanceof Exam) {
    return content.set('mostRecentAttempt', content.getIn(['mostRecentAttempt', 'id'], null));
  }
  return content;
}
export function normalizeDeeplyNestedLesson(deeplyNestedModule) {
  return deeplyNestedModule.merge({
    content: getChildrenIds(deeplyNestedModule.get('content'))
  });
}
export function normalizeContentListToContentById(contentFromResponse) {
  return mappifyByKey(contentFromResponse.map(normalizeContent));
}
export function normalizeMostRecentQuizAttempts(content) {
  return mappifyByKey(content.filter(thisContent => thisContent instanceof Quiz).map(quiz => quiz.get('mostRecentAttempt')).filter(attempt => attempt !== null));
}
export function normalizeMostRecentExamAttempts(response) {
  const attempt = response.getIn(['exam', 'mostRecentAttempt']);
  if (attempt) {
    return ImmutableMap({
      [attempt.get('id')]: attempt
    });
  }
  return ImmutableMap();
}
export function normalizeLessonResponse(response) {
  const contentFromResponse = response.get('content');
  return {
    lesson: normalizeDeeplyNestedLesson(response),
    contentById: normalizeContentListToContentById(contentFromResponse),
    quizAttemptsById: normalizeMostRecentQuizAttempts(contentFromResponse)
  };
}
function normalizeFromResponseUsingMethod(normalizationMethod, response) {
  return response.map(lesson => normalizationMethod(lesson.get('content'))).reduce((map, itemsById) => {
    return map.merge(itemsById);
  }, ImmutableMap());
}
export function normalizeLessonsResponse(response) {
  // response is a list of modules that we need to iterate through
  const lessonsById = mappifyByKey(response.map(normalizeDeeplyNestedLesson).filter(lesson => !lesson.get('content').isEmpty()));
  return {
    lessonsById,
    contentById: normalizeFromResponseUsingMethod(normalizeContentListToContentById, response),
    quizAttemptsById: normalizeFromResponseUsingMethod(normalizeMostRecentQuizAttempts, response)
  };
}
export function normalizeDeeplyNestedTrack(deeplyNestedTrack) {
  return deeplyNestedTrack.merge({
    lessons: getChildrenIds(deeplyNestedTrack.get('lessons')),
    introVideo: deeplyNestedTrack.getIn(['introVideo', 'id']),
    exam: deeplyNestedTrack.getIn(['exam', 'id'])
  });
}
export function normalizeTrackResponse(response) {
  const lessonsById = mappifyByKey(response.get('lessons').filter(lesson => !lesson.get('content').isEmpty()).map(normalizeDeeplyNestedLesson));
  const track = normalizeDeeplyNestedTrack(response);
  const trackHasInvalidLessons = lessonsById.count() < response.get('lessons').count();
  const trackWithoutEmptyLessons = trackHasInvalidLessons ? track.set('lessons', track.get('lessons').filter(lessonId => lessonsById.has(lessonId))) : track;
  const introVideo = response.get('introVideo');
  let contentById = normalizeFromResponseUsingMethod(normalizeContentListToContentById, response.get('lessons'));
  if (introVideo) {
    contentById = contentById.set(introVideo.get('id'), introVideo);
  }
  const exam = response.get('exam');
  if (exam) {
    contentById = contentById.set(exam.get('id'), normalizeContent(exam));
  }
  return {
    lessonsById,
    contentById,
    examAttemptsById: normalizeMostRecentExamAttempts(response),
    quizAttemptsById: normalizeFromResponseUsingMethod(normalizeMostRecentQuizAttempts, response.get('lessons')),
    tracksById: ImmutableMap({
      [track.get('id')]: trackWithoutEmptyLessons
    })
  };
}
export function normalizeTracksResponse(response) {
  const tracksByIdList = response.get('results').map(normalizeTrackResponse);
  return tracksByIdList.reduce((finalObject, thisTrackByIdResult) => {
    Object.keys(finalObject).forEach(key => {
      const value = thisTrackByIdResult[key];
      finalObject[key] = finalObject[key].merge(value);
    });
    return finalObject;
  }, {
    contentById: ImmutableMap(),
    examAttemptsById: ImmutableMap(),
    lessonsById: ImmutableMap(),
    quizAttemptsById: ImmutableMap(),
    tracksById: ImmutableMap()
  });
}